import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import { wrenchModeSchedulesRestCrudThunksForSupplier } from '../../thunks/wrench_mode_schedules_thunks';
import {
	WrenchHubUserAnalytic,
	fetchWrenchHubUserAnalyticsForSupplier,
} from '../../thunks/wrench_mode_events_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import { getSupervisorsForThirdPartySuppliers } from '../../thunks/contacts_thunks';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import WrenchModeLogsHeader, { WrenchModeLogsFilters } from './WrenchModeLogsHeader';
import WrenchModeLogsSidebar, { DateRange } from './WrenchModeLogsSidebar';
import WrenchModeLogsTable from './WrenchModeLogsTable';

require('./FieldTechsOverviewWrenchModeLogsPage.less');

const SCHEDULES_TARGET_COLLECTION = 'overviewWrenchModeSchedules';
const PAGE_SIZE = 10;
const SCHEDULES_SORTING = { sort_by: 'date', order: 'descend' };

const rangeToParams = (range: DateRange) => ({
	fromDate: range.from.clone().startOf('day').format('YYYY-MM-DD'),
	toDate: range.to.clone().endOf('day').format('YYYY-MM-DD'),
});

const buildScheduleParams = (range: DateRange, filters: WrenchModeLogsFilters) => {
	const { fromDate, toDate } = rangeToParams(range);
	const params: Record<string, any> = { fromDate, toDate };
	const technicianEmail = filters[FILTER_CONFIG_NAMES.TECHNICIAN];
	if (technicianEmail) {
		params.supplierEmail = technicianEmail;
	}
	const supervisorEmail = filters[FILTER_CONFIG_NAMES.SUPERVISOR];
	if (supervisorEmail) {
		params.supervisorEmail = supervisorEmail;
	}
	return params;
};

const parseInitialState = (search: string) => {
	const params = new URLSearchParams(search);
	const fromParam = params.get('from');
	const toParam = params.get('to');
	const legacyDate = params.get('date');
	const technicianEmail =
		params.get(FILTER_CONFIG_NAMES.TECHNICIAN) || params.get('supplierEmail') || undefined;
	const supervisorEmail = params.get(FILTER_CONFIG_NAMES.SUPERVISOR) || undefined;
	const locationIdsParam = params.get(FILTER_CONFIG_NAMES.LOCATION);

	let dateRange: DateRange;
	if (fromParam) {
		const from = moment(fromParam).startOf('day');
		const to = toParam ? moment(toParam).startOf('day') : from.clone();
		dateRange = { from, to };
	} else if (legacyDate) {
		const legacy = moment(legacyDate);
		dateRange = {
			from: legacy.clone().startOf('month'),
			to: legacy.clone().endOf('month'),
		};
	} else {
		const now = moment();
		dateRange = {
			from: now.clone().startOf('month'),
			to: now.clone().endOf('month'),
		};
	}

	return {
		dateRange,
		filters: {
			[FILTER_CONFIG_NAMES.TECHNICIAN]: technicianEmail,
			[FILTER_CONFIG_NAMES.SUPERVISOR]: supervisorEmail,
			[FILTER_CONFIG_NAMES.LOCATION]: locationIdsParam || undefined,
		} as WrenchModeLogsFilters,
	};
};

const FieldTechsOverviewWrenchModeLogsPage = (props: any) => {
	const {
		fetchSchedulesThunk,
		schedules,
		schedulesLoading,
		schedulesPagination,
		schedulesCount,
		fieldTechsState,
		fetchFieldTechs,
		fetchMultipleFieldTechs,
		allContactsState,
		fetchSupervisors,
		history,
	} = props;

	const initial = parseInitialState(history.location.search);
	const [dateRange, setDateRange] = useState<DateRange>(initial.dateRange);
	const [filters, setFilters] = useState<WrenchModeLogsFilters>(initial.filters);

	const [techAnalytics, setTechAnalytics] = useState<WrenchHubUserAnalytic[]>([]);
	const [techAnalyticsLoading, setTechAnalyticsLoading] = useState(false);

	// The analytics endpoint hydrates each technician's contact; the schedules payload behind
	// the table carries only an email. Sharing the contacts here means neither view has to
	// fetch them again. A technician absent from the analytics still falls back to a lookup.
	const contactsByEmail = useMemo(() => {
		const byEmail: Record<string, any> = {};
		for (const tech of techAnalytics) {
			if (tech.supplierContact && tech.supplierEmail) {
				byEmail[tech.supplierEmail] = tech.supplierContact;
			}
		}
		return byEmail;
	}, [techAnalytics]);

	// Sync state to query params so the URL is shareable / back-navigable
	useEffect(() => {
		const params = new URLSearchParams();
		params.set('from', dateRange.from.format('YYYY-MM-DD'));
		params.set('to', dateRange.to.format('YYYY-MM-DD'));
		const technicianEmail = filters[FILTER_CONFIG_NAMES.TECHNICIAN];
		if (technicianEmail) {
			params.set(FILTER_CONFIG_NAMES.TECHNICIAN, technicianEmail);
		}
		const supervisorEmail = filters[FILTER_CONFIG_NAMES.SUPERVISOR];
		if (supervisorEmail) {
			params.set(FILTER_CONFIG_NAMES.SUPERVISOR, supervisorEmail);
		}
		const locationIds = filters[FILTER_CONFIG_NAMES.LOCATION];
		if (locationIds) {
			params.set(FILTER_CONFIG_NAMES.LOCATION, locationIds);
		}
		const newSearch = params.toString();
		if (history.location.search !== '?' + newSearch) {
			history.replace({ pathname: history.location.pathname, search: newSearch });
		}
	}, [dateRange, filters, history]);

	const rangeKey = `${dateRange.from.format('YYYY-MM-DD')}/${dateRange.to.format('YYYY-MM-DD')}`;
	const supervisorKey = filters[FILTER_CONFIG_NAMES.SUPERVISOR] || '';
	const filtersKey = `${filters[FILTER_CONFIG_NAMES.TECHNICIAN] || ''}|${supervisorKey}`;
	const latestAnalyticsRequest = useRef<string>('');

	const fetchSchedulesForRange = useCallback(
		(range: DateRange, activeFilters: WrenchModeLogsFilters) => {
			const params = buildScheduleParams(range, activeFilters);
			return fetchSchedulesThunk(
				params,
				SCHEDULES_TARGET_COLLECTION,
				{ current: 1, pageSize: PAGE_SIZE },
				SCHEDULES_SORTING,
				null,
				false
			).catch(() => {
				/* errors tracked in redux via fetchError */
			});
		},
		[fetchSchedulesThunk]
	);

	// The analytics panel is always a supervisor-level aggregate, so it only takes
	// the supervisor filter. The technician filter intentionally narrows the
	// schedules list below but not these analytics — they roll up every tech under
	// the supervisor regardless of which technician is selected.
	const fetchAnalyticsForRange = useCallback((range: DateRange, supervisorEmail?: string) => {
		const { fromDate, toDate } = rangeToParams(range);
		const requestKey = `${fromDate}/${toDate}/${supervisorEmail || ''}`;
		latestAnalyticsRequest.current = requestKey;
		setTechAnalyticsLoading(true);

		fetchWrenchHubUserAnalyticsForSupplier(fromDate, toDate, supervisorEmail)
			.then((data) => {
				if (latestAnalyticsRequest.current !== requestKey) return;
				setTechAnalytics(data);
				setTechAnalyticsLoading(false);
			})
			.catch(() => {
				if (latestAnalyticsRequest.current !== requestKey) return;
				setTechAnalytics([]);
				setTechAnalyticsLoading(false);
			});
	}, []);

	// Initial load + refetch when range or filters change
	useEffect(() => {
		fetchSchedulesForRange(dateRange, filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rangeKey, filtersKey, fetchSchedulesForRange]);

	useEffect(() => {
		fetchAnalyticsForRange(dateRange, supervisorKey || undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rangeKey, supervisorKey, fetchAnalyticsForRange]);

	const handleRangeChange = useCallback((newRange: DateRange) => {
		setDateRange(newRange);
	}, []);

	const updateFilters = useCallback((newFilters: Record<string, any>) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
	}, []);

	const clearAndUpdateFilters = useCallback((newFilters: Record<string, any>) => {
		setFilters(newFilters);
	}, []);

	// Clicking a technician in the sidebar summary toggles that technician as the
	// active filter for the schedules list (click again to clear).
	const handleTechnicianSelect = useCallback((email: string) => {
		setFilters((prev) => ({
			...prev,
			[FILTER_CONFIG_NAMES.TECHNICIAN]:
				prev[FILTER_CONFIG_NAMES.TECHNICIAN] === email ? undefined : email,
		}));
	}, []);

	const handleLoadMore = useCallback(() => {
		const currentPage = nullSafeGetOrElse('current', schedulesPagination, 1);
		const params = buildScheduleParams(dateRange, filters);
		fetchSchedulesThunk(
			params,
			SCHEDULES_TARGET_COLLECTION,
			{ current: currentPage + 1, pageSize: PAGE_SIZE },
			SCHEDULES_SORTING,
			null,
			true
		).catch(() => {
			/* errors tracked in redux via fetchError */
		});
	}, [schedulesPagination, dateRange, filters, fetchSchedulesThunk]);

	const loadedCount = schedules.length;
	const hasMore = loadedCount > 0 && loadedCount < schedulesCount;
	const loadingMore = schedulesLoading && loadedCount > 0;
	const initialLoading = schedulesLoading && loadedCount === 0;

	const isDateFiltered = useMemo(() => {
		const fromStr = dateRange.from.format('YYYY-MM-DD');
		const toStr = dateRange.to.format('YYYY-MM-DD');
		const monthStart = dateRange.from.clone().startOf('month').format('YYYY-MM-DD');
		const monthEnd = dateRange.from.clone().endOf('month').format('YYYY-MM-DD');
		return fromStr !== monthStart || toStr !== monthEnd;
	}, [dateRange]);

	const handleClearDateFilter = useCallback(() => {
		setDateRange((prev) => ({
			from: prev.from.clone().startOf('month'),
			to: prev.from.clone().endOf('month'),
		}));
	}, []);

	return (
		<div className="wrenchModeLogsPage mx-xs flex flex-col gap-s">
			<WrenchModeLogsHeader
				filters={filters}
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				fieldTechsState={fieldTechsState}
				fetchFieldTechs={fetchFieldTechs}
				fetchMultipleFieldTechs={fetchMultipleFieldTechs}
				allContactsState={allContactsState}
				fetchSupervisors={fetchSupervisors}
				dateRange={dateRange}
				isDateFiltered={isDateFiltered}
				onClearDateFilter={handleClearDateFilter}
			/>
			<div className="flex gap-s">
				<WrenchModeLogsSidebar
					dateRange={dateRange}
					onRangeChange={handleRangeChange}
					techAnalytics={techAnalytics}
					loading={techAnalyticsLoading}
					selectedTechnicianEmail={filters[FILTER_CONFIG_NAMES.TECHNICIAN]}
					onTechnicianSelect={handleTechnicianSelect}
				/>
				<div className="flex-1 overflow-auto">
					<WrenchModeLogsTable
						schedules={schedules}
						contactsByEmail={contactsByEmail}
						loading={initialLoading}
						loadingMore={loadingMore}
						hasMore={hasMore}
						onLoadMore={handleLoadMore}
						filters={filters}
					/>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state: any) => {
	const schedulesState = state.wrench_mode_schedules || {};
	const coll = schedulesState[SCHEDULES_TARGET_COLLECTION];
	const ids: number[] = nullSafeGetOrElse('recordsIds', coll, []);
	const records = nullSafeGetOrElse('records', schedulesState, {});
	const schedulesList = ids.map((id) => records[id]).filter(Boolean);

	return {
		schedules: schedulesList,
		schedulesLoading: nullSafeGetOrElse('fetching', coll, false),
		schedulesPagination: nullSafeGetOrElse('pagination', coll, {
			current: 1,
			pageSize: PAGE_SIZE,
		}),
		schedulesCount: nullSafeGetOrElse('count', coll, 0),
		fieldTechsState: state.field_techs,
		allContactsState: state.all_contacts,
	};
};

const mapDispatchToProps = (dispatch: any) => ({
	fetchSchedulesThunk: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			wrenchModeSchedulesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchFieldTechs: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleFieldTechs: (ids: string[], targetCollectionName?: string) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchSupervisors: (
		params: any,
		targetCollectionName: string,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) =>
		dispatch(
			getSupervisorsForThirdPartySuppliers.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(FieldTechsOverviewWrenchModeLogsPage)
);
